import { useEffect, useState } from "react";
import { api, getToken } from "../lib/api";
import { useChild } from "../lib/childContext";
import ChildSwitcher from "../components/ChildSwitcher";
import SafeImage from "../components/SafeImage";
import { Sparkles, Sun, Moon, Star, Circle, Cloud, Heart, Leaf } from "lucide-react";

/** Some uploads are served by us behind auth — add the token as ?auth= so img
 *  tags can fetch them without a custom Authorization header. */
function authedSrc(url, token) {
  if (!url) return null;
  if (!url.startsWith("/api/files/") && !url.startsWith("http")) return url;
  if (!url.startsWith("/api/files/") || !token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}auth=${encodeURIComponent(token)}`;
}

const SIGNAL_ICON = {
  happy: Sun,
  calm: Circle,
  playful: Star,
  sleepy: Moon,
  shy: Cloud,
  curious: Leaf,
  need_hug: Heart,
  i_am_here: Circle,
};

const SIGNAL_LABEL = {
  happy: "feeling happy",
  calm: "feeling calm",
  playful: "feeling playful",
  sleepy: "feeling sleepy",
  shy: "feeling shy",
  curious: "feeling curious",
  need_hug: "wanting a hug",
  i_am_here: "saying I'm here",
};

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDay(iso) {
  return new Date(iso).toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export default function Moments() {
  const { selected } = useChild();
  const [moments, setMoments] = useState([]);
  const [selfChecks, setSelfChecks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!selected) {
      setMoments([]);
      setSelfChecks([]);
      setSummary(null);
      return;
    }
    api.get(`/children/${selected.id}/moments`).then((r) => setMoments(r.data));
    api
      .get(`/children/${selected.id}/self-check-ins`)
      .then((r) => setSelfChecks(r.data))
      .catch(() => setSelfChecks([]));
    setSummary(null);
  }, [selected]);

  const weave = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/children/${selected.id}/summary`);
      setSummary(r.data);
    } finally {
      setBusy(false);
    }
  };

  const grouped = moments.reduce((acc, m) => {
    const day = formatDay(m.created_at);
    (acc[day] = acc[day] || []).push({ ...m, kind: "moment" });
    return acc;
  }, {});

  // Interleave child self-check-ins as soft entries within their day
  for (const sc of selfChecks) {
    const day = formatDay(sc.at);
    (grouped[day] = grouped[day] || []).push({
      ...sc,
      kind: "self_check_in",
      created_at: sc.at,
    });
  }
  // Sort each day's items by time desc
  for (const day of Object.keys(grouped)) {
    grouped[day].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-4">
          <ChildSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-6 pb-12 fade-up">
        <span className="eyebrow">Today, gently</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          A quiet record of{" "}
          <em className="italic text-[#8A9984]">{selected?.name || "today"}.</em>
        </h1>

        <div className="mt-10 rounded-3xl border hairline bg-[#F2EFEA]/60 p-7 sm:p-9">
          <div className="flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">In one calm paragraph</span>
          </div>
          {summary ? (
            <p
              data-testid="moments-summary-text"
              className="mt-5 font-serif text-2xl italic text-[#2C2B29] leading-snug"
            >
              {summary.summary}
            </p>
          ) : (
            <>
              <p className="mt-5 text-base text-[#6A6863] font-light">
                Ask Alouvya to read today&rsquo;s moments back as a single, unhurried thought.
              </p>
              <button
                onClick={weave}
                disabled={busy || moments.length === 0}
                data-testid="moments-generate-summary"
                className="btn-primary mt-7"
              >
                {busy ? "Listening…" : "Weave today"}
                <Sparkles size={14} strokeWidth={1.5} />
              </button>
            </>
          )}
        </div>

        <section className="mt-16 space-y-16">
          {Object.entries(grouped).map(([day, items], gi) => (
            <div key={day} data-testid={`moments-day-${gi}`}>
              <div className="flex items-baseline justify-between mb-8">
                <h2 className="font-serif text-xl italic text-[#6A6863]">{day}</h2>
                <span className="eyebrow">{items.length} · moments</span>
              </div>
              <ol className="space-y-10">
                {items.map((m, i) => {
                  if (m.kind === "self_check_in") {
                    const Icon = SIGNAL_ICON[m.signal] || Circle;
                    return (
                      <li
                        key={m.id}
                        data-testid={`moment-${gi}-${i}-self`}
                        className="grid grid-cols-12 gap-4 sm:gap-6"
                      >
                        <div className="col-span-3 sm:col-span-2 pt-1">
                          <div className="eyebrow">{formatTime(m.created_at)}</div>
                          <div className="mt-2 text-xs italic text-[#8A9984] font-serif">
                            from {selected?.name}
                          </div>
                        </div>
                        <div className="col-span-9 sm:col-span-10 border-l-2 border-[#E6BFA6] pl-5 sm:pl-8 flex items-start gap-4">
                          <span
                            className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                            style={{ background: "#E6BFA6" }}
                          >
                            <Icon size={18} strokeWidth={1.5} className="text-white" />
                          </span>
                          <div>
                            <p className="font-serif text-xl sm:text-2xl leading-snug text-[#2C2B29]">
                              {selected?.name}{" "}
                              <em className="italic text-[#8A9984]">
                                {SIGNAL_LABEL[m.signal] || m.signal}.
                              </em>
                            </p>
                            <p className="mt-2 text-xs text-[#6A6863] tracking-wide">
                              tapped on the kiosk · facilitated by {m.by_user_name}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li
                      key={m.id}
                      data-testid={`moment-${gi}-${i}`}
                      className="grid grid-cols-12 gap-4 sm:gap-6"
                    >
                      <div className="col-span-3 sm:col-span-2 pt-1">
                        <div className="eyebrow">{formatTime(m.created_at)}</div>
                        <div className="mt-2 text-xs italic text-[#8A9984] font-serif">
                          {m.mood}
                        </div>
                      </div>
                      <div className="col-span-9 sm:col-span-10 border-l hairline pl-5 sm:pl-8">
                        {m.photo_url && (
                          <SafeImage
                            src={authedSrc(m.photo_url, getToken())}
                            alt={`Moment with ${selected?.name}`}
                            data-testid={`moment-${gi}-${i}-photo`}
                            loading="lazy"
                            className="mb-4 rounded-2xl max-w-full max-h-96 object-cover border hairline"
                          />
                        )}
                        <p className="font-serif text-xl sm:text-2xl leading-snug text-[#2C2B29]">
                          {m.text}
                        </p>
                        <p className="mt-3 text-xs text-[#6A6863] tracking-wide">
                          — {m.caregiver_name}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
          {moments.length === 0 && selfChecks.length === 0 && (
            <p
              data-testid="moments-empty"
              className="font-serif text-xl italic text-[#6A6863]"
            >
              No moments yet today. A still page is its own kind of news.
            </p>
          )}
        </section>
      </main>
    </div>
  );
}
